// src/components/auth/ChangePasswordForm.tsx

import React, { useState } from 'react';
import {
    Box,
    TextField,
    Button,
    InputAdornment,
    IconButton,
    Alert,
    Typography,
} from '@mui/material';
import {
    Lock as LockIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const ChangePasswordForm: React.FC = () => {
    const navigate = useNavigate();
    const { changePassword, logout } = useAuth();

    const [formData, setFormData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    });
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [serverError, setServerError] = useState<string>('');

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
        if (serverError) {
            setServerError('');
        }
    };

    const validate = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.currentPassword) {
            newErrors.currentPassword = 'Введите текущий пароль';
        }

        if (!formData.newPassword) {
            newErrors.newPassword = 'Введите новый пароль';
        } else if (formData.newPassword.length < 8) {
            newErrors.newPassword = 'Пароль должен быть минимум 8 символов';
        } else if (formData.newPassword === formData.currentPassword) {
            newErrors.newPassword = 'Новый пароль должен отличаться от текущего';
        }

        if (!formData.confirmPassword) {
            newErrors.confirmPassword = 'Подтвердите новый пароль';
        } else if (formData.newPassword !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Пароли не совпадают';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validate()) return;

        setLoading(true);
        setServerError('');

        try {
            await changePassword({
                currentPassword: formData.currentPassword,
                newPassword: formData.newPassword,
            });
            // Пароль изменён, пользователь разлогинен → на /login с сообщением
            navigate('/login', {
                replace: true,
                state: { message: 'Пароль успешно изменён. Войдите с новым паролем.' },
            });
        } catch (error: any) {
            if (error?.response?.status === 401 || error?.response?.status === 403) {
                setServerError('Текущий пароль неверен');
            } else if (error?.response?.data?.error) {
                setServerError(error.response.data.error);
            } else if (error?.message) {
                setServerError(error.message);
            } else {
                setServerError('Произошла ошибка. Попробуйте позже.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = async () => {
        await logout();
        navigate('/login', { replace: true });
    };

    return (
        <Box component="form" onSubmit={handleSubmit}>
            {serverError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {serverError}
                </Alert>
            )}

            <TextField
                fullWidth
                required
                label="Текущий пароль"
                name="currentPassword"
                type={showCurrent ? 'text' : 'password'}
                value={formData.currentPassword}
                onChange={handleChange}
                error={!!errors.currentPassword}
                helperText={errors.currentPassword || 'Пароль по умолчанию: 00000000'}
                disabled={loading}
                autoComplete="current-password"
                autoFocus
                sx={{ mb: 2 }}
                slotProps={{
                    input: {
                        startAdornment: (
                            <InputAdornment position="start">
                                <LockIcon color="action" />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    onClick={() => setShowCurrent(!showCurrent)}
                                    edge="end"
                                    size="small"
                                >
                                    {showCurrent ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    },
                }}
            />

            <TextField
                fullWidth
                required
                label="Новый пароль"
                name="newPassword"
                type={showNew ? 'text' : 'password'}
                value={formData.newPassword}
                onChange={handleChange}
                error={!!errors.newPassword}
                helperText={errors.newPassword || 'Минимум 8 символов'}
                disabled={loading}
                autoComplete="new-password"
                sx={{ mb: 2 }}
                slotProps={{
                    input: {
                        startAdornment: (
                            <InputAdornment position="start">
                                <LockIcon color="action" />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    onClick={() => setShowNew(!showNew)}
                                    edge="end"
                                    size="small"
                                >
                                    {showNew ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    },
                }}
            />

            <TextField
                fullWidth
                required
                label="Подтвердите новый пароль"
                name="confirmPassword"
                type={showConfirm ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={handleChange}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword}
                disabled={loading}
                autoComplete="new-password"
                sx={{ mb: 3 }}
                slotProps={{
                    input: {
                        startAdornment: (
                            <InputAdornment position="start">
                                <LockIcon color="action" />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    onClick={() => setShowConfirm(!showConfirm)}
                                    edge="end"
                                    size="small"
                                >
                                    {showConfirm ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    },
                }}
            />

            <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{ mb: 2 }}
            >
                {loading ? 'Сохранение...' : 'Сменить пароль'}
            </Button>

            <Button
                fullWidth
                variant="text"
                color="inherit"
                onClick={handleLogout}
                disabled={loading}
            >
                Выйти из аккаунта
            </Button>
        </Box>
    );
};

export default ChangePasswordForm;